import { pick } from "lodash-comms";
import {
  JoinTable,
  NonJoinTable,
  RecordTable,
  RecordValue,
  SingletonTagRecord,
  TableHasIdEqualToOtherTableId,
  TablePKey,
  tablePKeys,
  tablesWithIdEqualToOtherTableId,
} from "./schema";
import { deriveUUID, uuid } from "libs/uuid";
import { UnreachableCaseError } from "../errors";

// generateRecordId can also be used to generate IDs for certain types of
// documents embedded within jsonb columns. This type contains mappings for
// the props required to generate the IDs of these additional document types.
type AdditionalGeneratorIds = {
  // We don't simply use the group ID as the ID for draft_group_recipient_docs
  // because there's no requirement that group's and users don't have overlapping
  // IDs and we want each recipient to have a unique ID within a given draft.
  draft_group_recipient_doc: {
    type: "GROUP";
    group_id: string;
  };
  draft_user_recipient_doc: {
    type: "USER";
    user_id: string;
  };
  draft_label_doc: {
    type: "LABEL";
    label_id: string;
  };
  // we don't simply use the user ID as the ID for message_user_recipient_doc
  // because there's no requirement that group's and users don't have overlapping
  // IDs and we want each recipient to have a unique ID within a given draft.
  message_user_recipient_doc: {
    type: "USER";
    user_id: string;
  };
  message_group_recipient_doc: {
    type: "GROUP";
    group_id: string;
  };
  singleton_tag: {
    name: Lowercase<SingletonTagRecord["name"]>;
  };
};

/* -------------------------------------------------------------------------------------------------
 * generateRecordId
 * -----------------------------------------------------------------------------------------------*/

/** Generates a new ID for a record */
export function generateRecordId<Table extends JoinTable>(
  table: Table,
  record: Pick<RecordValue<Table>, TablePKey<Table>>,
): string;
export function generateRecordId<Table extends Exclude<NonJoinTable, TableHasIdEqualToOtherTableId>>(
  table: Table,
): string;
export function generateRecordId<Table extends keyof AdditionalGeneratorIds>(
  table: Table,
  record: AdditionalGeneratorIds[Table],
): string;
export function generateRecordId(table: string, record?: Record<string, unknown>): string {
  if (table in invalidTablesForGenerateRecordId) {
    throw new Error(`generateRecordId: invalid table "${table}"`);
  }

  if (record) {
    if (useLegacyGenerateRecordIdForTableMap[table as keyof typeof useLegacyGenerateRecordIdForTableMap]) {
      return legacyGenerateRecordId(table, record);
    } else {
      return newGenerateRecordId(table, record);
    }
  }

  return uuid();
}

/* -----------------------------------------------------------------------------------------------*/

function newGenerateRecordId(table: string, record?: Record<string, unknown>): string {
  const doesTableHaveCaseInsensitiveColumns = table in caseInsensitiveTableColumns;

  type AdditionalTable = keyof typeof additionalGeneratorPKeys;

  let pickedPKeys: Partial<Record<string, unknown>>;

  if (table in tablePKeys) {
    pickedPKeys = pick(record, tablePKeys[table as keyof typeof tablePKeys]);
  } else if (table in additionalGeneratorPKeys) {
    pickedPKeys = pick(record, additionalGeneratorPKeys[table as AdditionalTable]);
  } else {
    throw new UnreachableCaseError(table as never, `[newGenerateRecordId] invalid table "${table}"`);
  }

  if (!doesTableHaveCaseInsensitiveColumns) {
    return deriveUUID(...Object.entries(pickedPKeys).map(([key, value]) => key.toLowerCase() + String(value)));
  }

  type CaseInsensitiveTable = keyof typeof caseInsensitiveTableColumns;

  const caseInsensitiveColumns = caseInsensitiveTableColumns[table as CaseInsensitiveTable];

  const values = Object.entries(pickedPKeys).map(([key, value]) => {
    const stringValue = String(value);

    type Column = keyof typeof caseInsensitiveColumns;

    const normalizedStringValue = caseInsensitiveColumns[key as Column] ? stringValue.toLowerCase() : stringValue;

    return key.toLowerCase() + normalizedStringValue;
  });

  return deriveUUID(...values);
}

/* -----------------------------------------------------------------------------------------------*/

// This was a previous implementation of generateRecordId. It has a potential bug if a table's primary
// key is generated from two columns with values that can be swapped (e.g. tag_folder_member where the
// tag_id and folder_id values can be swapped). The problem is that the primary key is only generated from
// the sorted values of the columns, so swapping the values will result in the same primary key.
function legacyGenerateRecordId(table: string, record?: Record<string, unknown>): string {
  const doesTableHaveCaseInsensitiveColumns = table in caseInsensitiveTableColumns;

  type AdditionalTable = keyof typeof additionalGeneratorPKeys;

  let pickedPKeys: Partial<Record<string, unknown>>;

  if (table in tablePKeys) {
    pickedPKeys = pick(record, tablePKeys[table as keyof typeof tablePKeys]);
  } else if (table in additionalGeneratorPKeys) {
    pickedPKeys = pick(record, additionalGeneratorPKeys[table as AdditionalTable]);
  } else {
    throw new UnreachableCaseError(table as never, `[legacyGenerateRecordId] invalid table "${table}"`);
  }

  if (!doesTableHaveCaseInsensitiveColumns) {
    return deriveUUID(...Object.values(pickedPKeys).map(String));
  }

  type CaseInsensitiveTable = keyof typeof caseInsensitiveTableColumns;

  const caseInsensitiveColumns = caseInsensitiveTableColumns[table as CaseInsensitiveTable];

  const values = Object.entries(pickedPKeys).map(([key, value]) => {
    const stringValue = String(value);

    type Column = keyof typeof caseInsensitiveColumns;

    return caseInsensitiveColumns[key as Column] ? stringValue.toLowerCase() : stringValue;
  });

  return deriveUUID(...values);
}

/* -------------------------------------------------------------------------------------------------
 * utilities
 * -----------------------------------------------------------------------------------------------*/

const invalidTablesForGenerateRecordId = { ...tablesWithIdEqualToOtherTableId } as const;

const caseInsensitiveTableColumns = {
  organization_user_invitation: {
    email_address: true,
  },
  organization_controlled_domain: {
    domain: true,
  },
} satisfies {
  [Table in JoinTable]?: {
    [Column in keyof Pick<RecordValue<Table>, TablePKey<Table>>]?: boolean;
  };
};

const additionalGeneratorPKeys = {
  draft_group_recipient_doc: ["type", "group_id"],
  draft_user_recipient_doc: ["type", "user_id"],
  draft_label_doc: ["type", "label_id"],
  message_user_recipient_doc: ["type", "user_id"],
  message_group_recipient_doc: ["type", "group_id"],
  singleton_tag: ["name"],
} as const satisfies Record<keyof AdditionalGeneratorIds, string[]>;

// When `true`, that means that we should use the legacy generateRecordId
// function for that table. New tables added after this change should be excluded
// from this list.
const useLegacyGenerateRecordIdForTableMap = {
  auth_token: true,
  draft: true,
  draft_edit: true,
  inbox_entry: true,
  message: true,
  message_reactions: true,
  notification: true,
  organization: true,
  organization_controlled_domain: true,
  organization_profile: true,
  organization_user_invitation: true,
  organization_user_member: true,
  tag: true,
  tag_folder_member: true,
  tag_group_member: true,
  tag_metadata: true,
  tag_subscription: true,
  tag_user_member: true,
  thread: true,
  thread_group_permission: true,
  thread_read_receipt: true,
  thread_seen_receipt: true,
  thread_subscription: true,
  thread_tag: true,
  thread_timeline: true,
  thread_user_participant: true,
  thread_user_permission: true,
  user: true,
  user_contact_info: true,
  user_lesson: true,
  user_oauth: true,
  user_profile: true,
  user_push_notification_subscriptions: true,
  user_settings: true,
  // additional generator IDs
  draft_group_recipient_doc: true,
  draft_user_recipient_doc: true,
  draft_label_doc: true,
  message_user_recipient_doc: true,
  message_group_recipient_doc: true,
  singleton_tag: true,
} satisfies {
  [Table in RecordTable | keyof AdditionalGeneratorIds]?: true;
};
